/* eslint-disable react/no-array-index-key */
/* eslint-disable no-param-reassign */
/* eslint-disable no-restricted-properties */
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { API } from "../../apis/api"; // Adjust the import path as needed
import { filterFutureTime, validateFile } from "../../utils/utils";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1),
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[5],
    padding: 0,
    width: "800px",
    maxWidth: "90vw",
    maxHeight: "90vh",
    outline: "none",
    overflow: "auto",
  },
  header: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(3, 4),
    minHeight: theme.spacing(8),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "1.75rem",
    color: theme.palette.text.primary,
  },
  content: {
    padding: theme.spacing(2, 4, 3),
  },
  formGroup: {
    marginBottom: theme.spacing(3),
  },
  formLabel: {
    fontWeight: theme.typography.fontWeightBold,
    color: "#054f96",
    marginBottom: theme.spacing(1),
    display: "block",
    fontSize: "1rem",
  },
  fileInput: {
    display: "none",
  },
  fileInputLabel: {
    display: "flex",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    cursor: "pointer",
    transition: theme.transitions.create(["border-color"]),
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  fileInputText: {
    padding: theme.spacing(2),
    flexGrow: 1,
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
  },
  browseButton: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
    padding: theme.spacing(2, 4),
    borderRadius: 0,
    textTransform: "none",
    fontWeight: theme.typography.fontWeightMedium,
    "&:hover": {
      backgroundColor: theme.palette.grey[300],
    },
  },
  dateTimeInput: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      cursor: "pointer",
    },
  },
  previewContainer: {
    marginTop: theme.spacing(2),
  },
  previewBox: {
    width: 160,
    height: 120,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
    transition: theme.transitions.create(["box-shadow"]),
    "&:hover": {
      boxShadow: theme.shadows[2],
    },
  },
  previewIcon: {
    color: "#054f96",
    fontSize: "2.5rem",
    marginBottom: theme.spacing(0.5),
  },
  previewFileName: {
    textAlign: "center",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  deletePreviewButton: {
    position: "absolute",
    top: -12,
    right: -12,
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.secondary,
    width: 24,
    height: 24,
    padding: 0,
    boxShadow: theme.shadows[1],
    "&:hover": {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.error.main,
    },
  },
  actionButtons: {
    padding: theme.spacing(3, 4),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  cancelButton: {
    backgroundColor: theme.palette.grey[600],
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 4),
    textTransform: "none",
    fontWeight: theme.typography.fontWeightMedium,
    "&:hover": {
      backgroundColor: theme.palette.grey[700],
    },
  },
  uploadButton: {
    backgroundColor: "#00205b",
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 4),
    textTransform: "none",
    fontWeight: theme.typography.fontWeightMedium,
    "&:hover": {
      backgroundColor: "#001845",
    },
    "&:disabled": {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.grey[500],
    },
  },
  errorMessage: {
    color: theme.palette.error.main,
    fontSize: "0.875rem",
    marginTop: theme.spacing(1),
  },
  successMessage: {
    color: theme.palette.success.main,
    fontSize: "0.875rem",
    marginTop: theme.spacing(1),
  },
}));

const CustomDateTimeInput = React.forwardRef(
  ({ value, onClick, placeholder }, ref) => (
    <TextField
      ref={ref}
      variant="outlined"
      fullWidth
      value={value || ""}
      onClick={onClick}
      placeholder={placeholder}
      InputProps={{
        readOnly: true,
      }}
      className="cursor-pointer"
    />
  )
);

CustomDateTimeInput.displayName = "CustomDateTimeInput";

CustomDateTimeInput.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func,
  placeholder: PropTypes.string,
};
 
function UploadDocumentModal({ open, onClose, onUpload, ticketNumber }) {
  const classes = useStyles();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const userId = useSelector((state)=>state.auth.user.userId)

  const handleFileChange = (event) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const newFiles = Array.from(event.target.files);
    const validFiles = [];
    const errors = [];

    newFiles.forEach((file) => {
      try {
        validateFile(file);
        validFiles.push(file);
      } catch (error) {
        errors.push(error.message);
      }
    });

    if (errors.length > 0) {
      setUploadError(errors.join(" "));
      setUploadSuccess("");
    } else {
      setUploadError("");
      setUploadSuccess("Files uploaded successfully.");
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);
    }

    // Reset the input so the same file can be selected again
    event.target.value = "";
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const uploadDocumentToAPI = async (file, ticketCode, verifiedDate) => {
    try {
      const documentFormData = new FormData();
      documentFormData.append("file", file);
      documentFormData.append("ticket_code", ticketCode);
      documentFormData.append("document_type", "physically_verified");
      documentFormData.append("sub",userId)

      // Format date for API (ISO string format)
      if (verifiedDate) {
        documentFormData.append(
          "physically_verified_date",
          verifiedDate.toISOString()
        );
      }

      console.log("Uploading document with FormData:", {
        fileName: file.name,
        ticketNumber: ticketCode,
        documentType: "physically_verified",
        verifiedDate: verifiedDate ? verifiedDate.toISOString() : null,
      });

      const response = await API.post(
        "/document/upload-document/",
        documentFormData,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Document upload failed:", error);
      throw error;
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setUploadError("Please select at least one file to upload.");
      return;
    }

    if (!ticketNumber) {
      setUploadError("Ticket ID is required for upload.");
      return;
    }

    if (!selectedDate) {
      setUploadError("Please select the date and time of courier.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadError("");
      setUploadSuccess("");

      const uploadPromises = selectedFiles.map((file) =>
        uploadDocumentToAPI(file, ticketNumber, selectedDate)
      );

      const uploadResults = await Promise.all(uploadPromises);

      console.log("All uploads completed:", uploadResults);

      setUploadSuccess(
        `Successfully uploaded ${selectedFiles.length} document(s).`
      );

      // Call the onUpload callback if provided
      if (onUpload) {
        await onUpload({
          files: selectedFiles,
          date: selectedDate,
          results: uploadResults,
        });
      }

      // Reset form after successful upload
      setTimeout(() => {
        setSelectedFiles([]);
        setSelectedDate(null);
        setUploadSuccess("");
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadError(
        error.response?.data?.message ||
          error.message ||
          "Upload failed. Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFiles([]);
      setSelectedDate(null);
      setUploadError("");
      setUploadSuccess("");
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <Modal
      className={classes.modal}
      open={open}
      onClose={handleClose}
      aria-labelledby="upload-document-modal"
      disableBackdropClick={isUploading}
      disableEscapeKeyDown={isUploading}
    >
      <div className={classes.paper}>
        <div className={classes.header}>
          <Typography
            className={classes.title}
            variant="h4"
            id="upload-document-modal"
          >
            Upload Physical Document
          </Typography>
          <IconButton
            onClick={handleClose}
            aria-label="close"
            disabled={isUploading}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <Divider />

        <div className={classes.content}>
          <Grid container spacing={4}>
            <Grid item xs={8} md={8}>
              <div className={classes.formGroup}>
                <Typography
                  className={classes.formLabel}
                  variant="subtitle1"
                  component="label"
                >
                  Courier Images *
                </Typography>
                <input
                  accept="image/*,.pdf,.doc,.docx"
                  className={classes.fileInput}
                  id="courier-image-upload"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
                <label
                  htmlFor="courier-image-upload"
                  className={classes.fileInputLabel}
                >
                  <div className={classes.fileInputText}>
                    {selectedFiles.length > 0
                      ? `${selectedFiles.length} file${
                          selectedFiles.length > 1 ? "s" : ""
                        } selected`
                      : "Choose files..."}
                  </div>
                  <Button
                    component="span"
                    className={classes.browseButton}
                    disableElevation
                    disabled={isUploading}
                  >
                    Browse
                  </Button>
                </label>

                {selectedFiles.length > 0 && (
                  <div className={classes.previewContainer}>
                    <Grid container spacing={2}>
                      {selectedFiles.map((file, index) => (
                        <Grid item key={`${file.name}-${index}`}>
                          <div className={classes.previewBox}>
                            <FileCopyIcon className={classes.previewIcon} />
                            <Typography
                              className={classes.previewFileName}
                              title={`${file.name} (${formatFileSize(
                                file.size
                              )})`}
                            >
                              {file.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              style={{ fontSize: "0.6rem" }}
                            >
                              {formatFileSize(file.size)}
                            </Typography>
                            <IconButton
                              className={classes.deletePreviewButton}
                              size="small"
                              onClick={() => handleRemoveFile(index)}
                              aria-label={`Remove ${file.name}`}
                              disabled={isUploading}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </Grid>
                      ))}
                    </Grid>
                  </div>
                )}
              </div>
            </Grid>

            <Grid item xs={4} md={4}>
              <div className={classes.formGroup}>
                <Typography
                  className={classes.formLabel}
                  variant="subtitle1"
                  component="label"
                >
                  Date and Time of Courier *
                </Typography>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={1}
                  dateFormat="dd/MM/yyyy - HH:mm"
                  placeholderText="DD/MM/YYYY - HH:MM"
                  customInput={<CustomDateTimeInput />}
                  disabled={isUploading}
                  maxDate={new Date()}
                  filterTime={filterFutureTime}
                />
              </div>
            </Grid>

            {/* Display error or success messages */}
            {uploadError && (
              <Grid item xs={12}>
                <Typography className={classes.errorMessage}>
                  {uploadError}
                </Typography>
              </Grid>
            )}

            {uploadSuccess && (
              <Grid item xs={12}>
                <Typography className={classes.successMessage}>
                  {uploadSuccess}
                </Typography>
              </Grid>
            )}
          </Grid>
        </div>

        <div className={classes.actionButtons}>
          <Grid container style={{ justifyContent: "flex-end" }} spacing={2}>
            <Grid item>
              <Button
                className={classes.cancelButton}
                variant="contained"
                disableElevation
                onClick={handleClose}
                disabled={isUploading}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                className={classes.uploadButton}
                variant="contained"
                disableElevation
                onClick={handleUpload}
                disabled={
                  selectedFiles.length === 0 || !selectedDate || isUploading
                }
              >
                {isUploading ? "Uploading..." : "Upload"}
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>
    </Modal>
  );
}

UploadDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
  ticketNumber: PropTypes.string,
};

UploadDocumentModal.defaultProps = {
  onUpload: null,
};

export default UploadDocumentModal;
